import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'

import { fetchSLAs, addSLA, updateSLA, deleteSLA } from '../actions/slas'

import '../styles/SLAView.css'

const ZONE_OPTIONS = [
  { value: 'ES_PENINSULA', label: 'España Península' },
  { value: 'ES_ISLANDS', label: 'España Islas (Baleares/Canarias)' },
  { value: 'EU_ZONE1', label: 'Europa Zona 1 (Francia/Portugal/Italia/Alemania)' },
  { value: 'EU_ZONE2', label: 'Europa Zona 2 (Resto Países)' },
]

const PRIORITY_COLUMNS = [
  {
    key: 'low',
    label: 'Consulta general',
    sublabel: 'LOW',
    color: '#9e9e9e',
    fields: ['maxResponseTime'],
    fieldLabels: ['Tiempo máx. respuesta'],
  },
  {
    key: 'medium',
    label: 'Incidente menor',
    sublabel: 'MEDIUM',
    color: '#1976d2',
    fields: ['maxResponseTime'],
    fieldLabels: ['Tiempo máx. respuesta'],
  },
  {
    key: 'high',
    label: 'Avería',
    sublabel: 'HIGH',
    color: '#ef6c00',
    fields: ['maxResponseTime', 'maxRepairTimeMin', 'maxRepairTimeMax', 'estRepairWithPromoter'],
    fieldLabels: ['Tiempo máx. respuesta', 'Tiempo máx. reparación (mín)', 'Tiempo máx. reparación (máx)', 'Est. reparación con promotor'],
  },
  {
    key: 'critical',
    label: 'Avería urgente',
    sublabel: 'CRITICAL',
    color: '#c62828',
    fields: ['maxResponseTime', 'maxRepairTimeMin', 'maxRepairTimeMax', 'estRepairWithPromoter'],
    fieldLabels: ['Tiempo máx. respuesta', 'Tiempo máx. reparación (mín)', 'Tiempo máx. reparación (máx)', 'Est. reparación con promotor'],
  },
]

// Default SLA values per the reference table
const DEFAULT_SLA_VALUES = {
  ES_PENINSULA: {
    low: { maxResponseTime: 24 },
    medium: { maxResponseTime: 24 },
    high: { maxResponseTime: 4, maxRepairTimeMin: 48, maxRepairTimeMax: 72, estRepairWithPromoter: 4 },
    critical: { maxResponseTime: 2, maxRepairTimeMin: 24, maxRepairTimeMax: 48, estRepairWithPromoter: 2 },
  },
  ES_ISLANDS: {
    low: { maxResponseTime: 24 },
    medium: { maxResponseTime: 24 },
    high: { maxResponseTime: 4, maxRepairTimeMin: 72, maxRepairTimeMax: 96, estRepairWithPromoter: 4 },
    critical: { maxResponseTime: 2, maxRepairTimeMin: 48, maxRepairTimeMax: 72, estRepairWithPromoter: 2 },
  },
  EU_ZONE1: {
    low: { maxResponseTime: 24 },
    medium: { maxResponseTime: 24 },
    high: { maxResponseTime: 4, maxRepairTimeMin: 72, maxRepairTimeMax: 96, estRepairWithPromoter: 4 },
    critical: { maxResponseTime: 2, maxRepairTimeMin: 48, maxRepairTimeMax: 72, estRepairWithPromoter: 2 },
  },
  EU_ZONE2: {
    low: { maxResponseTime: 24 },
    medium: { maxResponseTime: 24 },
    high: { maxResponseTime: 4, maxRepairTimeMin: 96, maxRepairTimeMax: 120, estRepairWithPromoter: 4 },
    critical: { maxResponseTime: 2, maxRepairTimeMin: 72, maxRepairTimeMax: 96, estRepairWithPromoter: 2 },
  },
}

class SLAView extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      editModalOpen: false,
      editingSLA: null,
      formData: {},
      deleteConfirmOpen: false,
      deleteId: null,
      addModalOpen: false,
    }
  }

  componentDidMount() {
    this.props.fetchSLAs()
  }

  componentDidUpdate(prevProps) {
    if (this.props.slaNeedRefresh && !prevProps.slaNeedRefresh) {
      this.props.fetchSLAs()
    }
  }

  // Build an empty form data structure
  getEmptyFormData(zone = '') {
    const data = { zone }
    PRIORITY_COLUMNS.forEach(col => {
      col.fields.forEach(field => {
        data[`${col.key}_${field}`] = ''
      })
    })
    return data
  }

  // Build form data from an existing SLA record
  slaToFormData(sla) {
    const data = { zone: sla.zone }
    PRIORITY_COLUMNS.forEach(col => {
      col.fields.forEach(field => {
        data[`${col.key}_${field}`] = sla[col.key] && sla[col.key][field] !== undefined ? sla[col.key][field] : ''
      })
    })
    return data
  }

  // Convert form data back to API structure
  formDataToSLA(formData) {
    const sla = { zone: formData.zone }
    PRIORITY_COLUMNS.forEach(col => {
      sla[col.key] = {}
      col.fields.forEach(field => {
        const val = formData[`${col.key}_${field}`]
        sla[col.key][field] = val !== '' ? Number(val) : null
      })
    })
    return sla
  }

  openAddModal() {
    this.setState({
      addModalOpen: true,
      formData: this.getEmptyFormData(),
    })
  }

  openEditModal(sla) {
    this.setState({
      editModalOpen: true,
      editingSLA: sla,
      formData: this.slaToFormData(sla),
    })
  }

  closeModals() {
    this.setState({
      editModalOpen: false,
      addModalOpen: false,
      editingSLA: null,
      formData: {},
      deleteConfirmOpen: false,
      deleteId: null,
    })
  }

  handleFormChange(key, value) {
    this.setState(prev => ({
      formData: { ...prev.formData, [key]: value }
    }))
  }

  handleZoneChange(zone) {
    // When zone changes, auto-fill with default values if available
    const defaults = DEFAULT_SLA_VALUES[zone]
    if (defaults) {
      const data = { zone }
      PRIORITY_COLUMNS.forEach(col => {
        col.fields.forEach(field => {
          data[`${col.key}_${field}`] = defaults[col.key] && defaults[col.key][field] !== undefined
            ? defaults[col.key][field]
            : ''
        })
      })
      this.setState({ formData: data })
    } else {
      this.handleFormChange('zone', zone)
    }
  }

  handleSave() {
    const { formData, editingSLA } = this.state
    const slaData = this.formDataToSLA(formData)

    if (editingSLA) {
      this.props.updateSLA(editingSLA._id, slaData)
    } else {
      this.props.addSLA(slaData)
    }
    this.closeModals()
  }

  handleDelete(id) {
    this.setState({ deleteConfirmOpen: true, deleteId: id })
  }

  confirmDelete() {
    this.props.deleteSLA(this.state.deleteId)
    this.closeModals()
  }

  getZoneLabel(zoneValue) {
    const opt = ZONE_OPTIONS.find(z => z.value === zoneValue)
    return opt ? opt.label : zoneValue
  }

  formatHours(val) {
    if (val === null || val === undefined || val === '') return '--'
    return `${val}h`
  }

  formatRange(min, max) {
    if ((min === null || min === undefined) && (max === null || max === undefined)) return '--'
    if (min !== null && max !== null) return `${min} - ${max}h`
    return this.formatHours(min || max)
  }

  renderSLATable() {
    const { slas, loading } = this.props

    if (loading) return <LoadingProgress />
    if (!slas || slas.length === 0) {
      return (
        <div className="sla-empty">
          <i className="fas fa-shield-alt"></i>
          <p>No hay SLAs configurados</p>
          <p className="sla-empty-hint">Añade SLAs para definir los tiempos de respuesta por zona geográfica</p>
        </div>
      )
    }

    return (
      <div className="sla-table-wrapper">
        <table className="sla-table">
          <thead>
            <tr>
              <th rowSpan="2" className="sla-th-zone">Zona</th>
              <th colSpan="1" className="sla-th-priority low">
                <div className="sla-th-priority-label">
                  <span className="sla-priority-dot low"></span>
                  Consulta general
                </div>
                <div className="sla-th-priority-sub">LOW</div>
              </th>
              <th colSpan="1" className="sla-th-priority medium">
                <div className="sla-th-priority-label">
                  <span className="sla-priority-dot medium"></span>
                  Incidente menor
                </div>
                <div className="sla-th-priority-sub">MEDIUM</div>
              </th>
              <th colSpan="3" className="sla-th-priority high">
                <div className="sla-th-priority-label">
                  <span className="sla-priority-dot high"></span>
                  Avería
                </div>
                <div className="sla-th-priority-sub">HIGH</div>
              </th>
              <th colSpan="3" className="sla-th-priority critical">
                <div className="sla-th-priority-label">
                  <span className="sla-priority-dot critical"></span>
                  Avería urgente
                </div>
                <div className="sla-th-priority-sub">CRITICAL</div>
              </th>
              <th rowSpan="2" className="sla-th-actions">Acciones</th>
            </tr>
            <tr>
              {/* LOW */}
              <th className="sla-th-sub">Máx. respuesta</th>
              {/* MEDIUM */}
              <th className="sla-th-sub">Máx. respuesta</th>
              {/* HIGH */}
              <th className="sla-th-sub">Máx. respuesta</th>
              <th className="sla-th-sub">Máx. reparación</th>
              <th className="sla-th-sub">Est. con promotor</th>
              {/* CRITICAL */}
              <th className="sla-th-sub">Máx. respuesta</th>
              <th className="sla-th-sub">Máx. reparación</th>
              <th className="sla-th-sub">Est. con promotor</th>
            </tr>
          </thead>
          <tbody>
            {slas.map(sla => (
              <tr key={sla._id} className="sla-row">
                <td className="sla-td-zone">
                  <div className="sla-zone-name">{this.getZoneLabel(sla.zone)}</div>
                </td>
                {/* LOW */}
                <td className="sla-td-value">{this.formatHours(sla.low && sla.low.maxResponseTime)}</td>
                {/* MEDIUM */}
                <td className="sla-td-value">{this.formatHours(sla.medium && sla.medium.maxResponseTime)}</td>
                {/* HIGH */}
                <td className="sla-td-value">{this.formatHours(sla.high && sla.high.maxResponseTime)}</td>
                <td className="sla-td-value">
                  {this.formatRange(
                    sla.high && sla.high.maxRepairTimeMin,
                    sla.high && sla.high.maxRepairTimeMax
                  )}
                </td>
                <td className="sla-td-value">{this.formatHours(sla.high && sla.high.estRepairWithPromoter)}</td>
                {/* CRITICAL */}
                <td className="sla-td-value">{this.formatHours(sla.critical && sla.critical.maxResponseTime)}</td>
                <td className="sla-td-value">
                  {this.formatRange(
                    sla.critical && sla.critical.maxRepairTimeMin,
                    sla.critical && sla.critical.maxRepairTimeMax
                  )}
                </td>
                <td className="sla-td-value">{this.formatHours(sla.critical && sla.critical.estRepairWithPromoter)}</td>
                {/* Actions */}
                <td className="sla-td-actions">
                  <button className="sla-action-btn edit" onClick={() => this.openEditModal(sla)}>
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                  <button className="sla-action-btn delete" onClick={() => this.handleDelete(sla._id)}>
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  renderFormModal() {
    const { editModalOpen, addModalOpen, formData, editingSLA } = this.state
    const isOpen = editModalOpen || addModalOpen
    const isEditing = !!editingSLA
    const title = isEditing ? 'Editar SLA' : 'Nuevo SLA'

    // Check which zones are already used
    const usedZones = (this.props.slas || []).map(s => s.zone)
    const availableZones = isEditing
      ? ZONE_OPTIONS
      : ZONE_OPTIONS.filter(z => !usedZones.includes(z.value))

    return (
      <Modal isOpen={isOpen} toggle={() => this.closeModals()} className="sla-form-modal" size="lg">
        <ModalHeader toggle={() => this.closeModals()}>
          <i className="fas fa-shield-alt"></i> {title}
        </ModalHeader>
        <ModalBody>
          {/* Zone selector */}
          <div className="sla-form-zone">
            <label className="sla-form-label">Zona Geográfica</label>
            <select
              className="sla-form-select"
              value={formData.zone || ''}
              onChange={e => this.handleZoneChange(e.target.value)}
              disabled={isEditing}
            >
              <option value="">Seleccionar zona...</option>
              {availableZones.map(z => (
                <option key={z.value} value={z.value}>{z.label}</option>
              ))}
            </select>
          </div>

          {/* Priority fields */}
          <div className="sla-form-priorities">
            {PRIORITY_COLUMNS.map(col => (
              <div key={col.key} className={`sla-form-priority-card ${col.key}`}>
                <div className="sla-form-priority-header" style={{ borderLeftColor: col.color }}>
                  <span className="sla-form-priority-name">{col.label}</span>
                  <span className="sla-form-priority-level">{col.sublabel}</span>
                </div>
                <div className="sla-form-priority-fields">
                  {col.fields.map((field, i) => (
                    <div key={field} className="sla-form-field">
                      <label className="sla-form-field-label">{col.fieldLabels[i]}</label>
                      <div className="sla-form-field-input-group">
                        <input
                          type="number"
                          className="sla-form-field-input"
                          value={formData[`${col.key}_${field}`] || ''}
                          onChange={e => this.handleFormChange(`${col.key}_${field}`, e.target.value)}
                          placeholder="0"
                          min="0"
                          step="1"
                        />
                        <span className="sla-form-field-unit">h</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => this.closeModals()}>Cancelar</Button>
          <Button
            color="primary"
            onClick={() => this.handleSave()}
            disabled={!formData.zone}
          >
            {isEditing ? 'Guardar cambios' : 'Crear SLA'}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  renderDeleteConfirm() {
    const { deleteConfirmOpen } = this.state
    return (
      <Modal isOpen={deleteConfirmOpen} toggle={() => this.closeModals()} size="sm">
        <ModalHeader toggle={() => this.closeModals()}>
          Confirmar eliminación
        </ModalHeader>
        <ModalBody>
          ¿Estás seguro de que deseas eliminar este SLA? Esta acción no se puede deshacer.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => this.closeModals()}>Cancelar</Button>
          <Button color="danger" onClick={() => this.confirmDelete()}>Eliminar</Button>
        </ModalFooter>
      </Modal>
    )
  }

  render() {
    const { error, role, type } = this.props

    // Only ADMIN + MASTER can see this view
    if (role !== 'ADMIN' || type !== 'MASTER') {
      return (
        <div className="sla-container">
          <div className="sla-empty">
            <i className="fas fa-lock"></i>
            <p>No tienes permisos para acceder a esta página</p>
          </div>
        </div>
      )
    }

    return (
      <div className="sla-container">
        {error && <ErrorMessage message={error} />}

        {/* Header */}
        <div className="sla-header">
          <div className="sla-header-content">
            <div className="sla-header-text">
              <h1><i className="fas fa-shield-alt"></i> Acuerdos de Nivel de Servicio (SLA)</h1>
              <p>Define los tiempos máximos de respuesta y reparación por zona geográfica y prioridad</p>
            </div>
            <div className="sla-header-actions">
              <button className="sla-add-btn" onClick={() => this.openAddModal()}>
                <i className="fas fa-plus"></i> Nuevo SLA
              </button>
            </div>
          </div>
        </div>

        {/* Reference legend */}
        <div className="sla-legend">
          <div className="sla-legend-title">
            <i className="fas fa-info-circle"></i> Referencia de prioridades
          </div>
          <div className="sla-legend-items">
            <div className="sla-legend-item">
              <span className="sla-priority-dot low"></span>
              <strong>LOW</strong> — Consulta general
            </div>
            <div className="sla-legend-item">
              <span className="sla-priority-dot medium"></span>
              <strong>MEDIUM</strong> — Incidente menor
            </div>
            <div className="sla-legend-item">
              <span className="sla-priority-dot high"></span>
              <strong>HIGH</strong> — Avería
            </div>
            <div className="sla-legend-item">
              <span className="sla-priority-dot critical"></span>
              <strong>CRITICAL</strong> — Avería urgente
            </div>
          </div>
        </div>

        {/* SLA Table */}
        {this.renderSLATable()}

        {/* Modals */}
        {this.renderFormModal()}
        {this.renderDeleteConfirm()}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  slas: state.slas.slas,
  sla: state.slas.sla,
  loading: state.slas.loading,
  error: state.slas.error,
  slaNeedRefresh: state.slas.slaNeedRefresh,
  role: state.auth.role,
  type: state.auth.type,
})

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchSLAs,
  addSLA,
  updateSLA,
  deleteSLA,
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(SLAView)
